import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGSAP } from '@/composables/useGSAP'
import { useImageFormatContext } from '@/composables/useImageFormatContext'
import type { Persona } from '@/state/AppConfig'
import { getContentfulUrl } from '@/utils/contentful'

export type ProfileProps = {
  className?: string
  persona: Persona
  appear?: boolean
  delay?: number
  noAnimation?: boolean
}

export default function Profile({ className, persona, appear = false, delay = 0, noAnimation = false }: ProfileProps) {
  const { t } = useTranslation()
  const { gsap } = useGSAP()

  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (noAnimation) return
      if (appear) {
        gsap
          .timeline({
            delay,
          })
          .fromTo(
            ref.current,
            {
              opacity: 0,
              x: 20,
            },
            {
              opacity: 1,
              x: 0,
              duration: 0.5,
              ease: 'power2.out',
            }
          )
      }
    })

    return () => ctx.kill()
  }, [gsap, delay, appear, ref])

  const betterFormat = useImageFormatContext()
  const [imageUrl, setImageUrl] = useState(null)
  useEffect(() => {
    const url = getContentfulUrl(`/src/assets/images/chat/${persona}.png`, {
      w: 80,
      fm: betterFormat,
    })
    setImageUrl(url)
  }, [persona, betterFormat])

  return (
    <div className={`${className}`}>
      <div ref={ref} className='Profile mt-auto md:mt-none inline-flex gap-8 md:gap-16 items-center'>
        <img className='block w-40 h-40' src={imageUrl} alt={t(`child.${persona}.name`)} />
        <span className='h-24 px-8 flex items-center justify-center rounded-4 bg-secondary antialiased font-semibold leading-relaxed text-18'>
          {t(`child.${persona}.name`)}
        </span>
      </div>
    </div>
  )
}
